/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the variant state (Masters Ladder R3 §2.1–2.5).

   One click on the variant bar recomposes the sheet; from then on every
   keystroke lands on the ACTIVE layer. This hook is the wiring between
   those two facts:

     · which variant is showing (persisted per master, workspace-state law)
     · the canvas document for the current variant (compose, R1 brain)
     · the baseline every edit is measured against, rebuilt on entry so
       office-master edits RIPPLE into every variant (§1.3)
     · the derive: doc → layer ops, on the one module that knows how

   Cohesion R1 Phase 3: a variant is a freeform-named starting point, not a
   client picker — creating one asks for a name, and duplicating one copies
   its authorship (adds/overrides/hides) into a new named layer. The old
   `clients` table (migration 044) is orphaned by this flow; it is left in
   place, never read or written here.

   The Office Master variant is the R2 Studio, untouched: `coreContent`
   only, no layer paint, no provenance, no guard.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import type { JSONContent } from '@tiptap/core';
import type { ClientLayer, ContentBlock } from '../../shared/types';
import { makeOverrideBase } from '../../shared/spec-compose';
import {
  buildBaseline,
  buildVariantView,
  composeVariant,
  deriveLayer,
  duplicateVariantLayer,
  variantDocBlocks,
  newClientLayer,
  sameLayer,
  staleOverrideBlockIds,
  type VariantBaseline,
  type VariantView,
} from './variantOps';
import { hideSelectedBlocks, revertBlockToCore, setBlockGhost } from './variantCommands';
import { VARIANT_REBUILD, type VariantGateReason } from './variantPlugin';
import type { SpecMasterDraftApi } from './useSpecMasterDraft';
import { readPref, writePref } from '../lib/usePref';
import { Prefs } from '../services/prefsService';

const VARIANT_KEY = 'sf.specStudioVariant.v1';

/** Which variant this user last had open on this master. A workspace
 *  preference, so it lives where the other Studio view state lives — D1
 *  cross-surface parity: cloud `user_prefs` + localStorage fast cache, the
 *  whole per-master map stored as one pref value (plain functions, not the
 *  `usePref` hook, since this is called from outside React render). */
function readVariantPref(masterId: string): string | null {
  const map = readPref<Record<string, string>>(VARIANT_KEY, VARIANT_KEY, {});
  return map[masterId] ?? null;
}

function writeVariantPref(masterId: string, layerId: string | null): void {
  const map = readPref<Record<string, string>>(VARIANT_KEY, VARIANT_KEY, {});
  if (layerId) map[masterId] = layerId;
  else delete map[masterId];
  writePref(VARIANT_KEY, VARIANT_KEY, map);
}

export interface StaleCompare {
  blockId: string;
  core: string;
  variant: string;
  anchor: { left: number; bottom: number };
}

export interface VariantApi {
  layers: ClientLayer[];
  activeLayerId: string | null;
  activeLayer: ClientLayer | null;
  /** Identity of the loaded document — masterId + variant. Rebuilds the canvas. */
  docKey: string;
  /** False for the one frame before the remembered variant has resolved. */
  ready: boolean;
  /** What the canvas loads for the current variant. */
  docBlocks: ContentBlock[];
  /** Read by the canvas plugin at decoration time. */
  getView: () => VariantView | null;
  /** True when a variant's layer is being painted on. */
  inVariant: boolean;
  /** Provenance of one block, for the context menu. */
  originOf: (blockId: string) => 'core' | 'layer';
  isOverridden: (blockId: string) => boolean;
  isGhost: (blockId: string) => boolean;

  switchTo: (layerId: string | null) => void;
  /** Re-compose the CURRENT variant — the sheet after a pick moved (R4). A pick
   *  is client data, not a document edit, so it cannot be expressed as a
   *  transaction on the layer; the variant is rebuilt instead. Nothing is lost:
   *  core edits live in the draft's block ref and layer edits are already
   *  derived, so the rebuild reads the newest of both. */
  recompose: () => void;
  /** "＋" on the variant bar — a blank starting point with a freeform name. */
  createVariant: (name: string) => void;
  /** Copy the selected variant's authorship (adds/overrides/hides) into a new,
   *  freshly-named layer — a starting point, not a link. */
  duplicateVariant: (layerId: string, name: string) => void;
  deleteVariant: (layerId: string) => void;

  /** Canvas change handler for a client variant. */
  applyDoc: (doc: JSONContent) => void;
  onGate: (reason: VariantGateReason) => void;
  onTag: (kind: 'override' | 'stale' | 'restore', blockId: string, anchor: DOMRect) => void;

  hideSelection: () => void;
  setGhost: (blockId: string, ghost: boolean) => void;
  revert: (blockId: string) => void;
  compare: StaleCompare | null;
  closeCompare: () => void;
  acknowledgeStale: (blockId: string) => void;
}

export function useVariant(
  masterId: string,
  draft: SpecMasterDraftApi,
  getEditor: () => Editor | null,
  toast: (msg: string, isError?: boolean) => void
): VariantApi {
  const [activeLayerId, setActiveLayerId] = React.useState<string | null>(null);
  // An empty key means "the variant has not resolved yet" — the canvas waits one
  // frame rather than mounting an editor on a document nobody asked for.
  const [docState, setDocState] = React.useState<{ key: string; blocks: ContentBlock[] }>({
    key: '',
    blocks: [],
  });
  const [compare, setCompare] = React.useState<StaleCompare | null>(null);

  const baselineRef = React.useRef<VariantBaseline | null>(null);
  const viewRef = React.useRef<VariantView | null>(null);
  const activeRef = React.useRef<string | null>(null);
  activeRef.current = activeLayerId;
  const toastRef = React.useRef(toast);
  toastRef.current = toast;
  const gateAt = React.useRef(0);
  const nonce = React.useRef(0);

  const layers = draft.layers;

  /* ─── Switching variants ─── */
  const switchTo = React.useCallback(
    (layerId: string | null) => {
      // A variant shows the PUBLISHED master through its layer — never the
      // draft being typed on the Office Master tab (docs/MODULES_VISION.md:
      // "propagation = PR, never silent mutation"). A master edit reaches a
      // variant only by publishing, and then it arrives as an offer in the
      // Updates panel. The Office Master tab itself still shows the draft.
      const published = draft.master?.coreContent;
      const core = layerId && published ? published : draft.currentBlocks();
      if (!layerId) {
        baselineRef.current = null;
        viewRef.current = null;
        setActiveLayerId(null);
        writeVariantPref(masterId, null);
        setDocState({ key: `${masterId}::core::${nonce.current++}`, blocks: core });
        return;
      }
      const layer = draft.currentLayers().find((l) => l.id === layerId);
      if (!layer) return;
      // Rebuilt on EVERY entry: the office master may have moved since the
      // last visit, and a variant must show that ripple (§1.3).
      const baseline = buildBaseline(core, masterId);
      const result = composeVariant(core, masterId, layer);
      baselineRef.current = baseline;
      viewRef.current = buildVariantView(layer, baseline, layer.name || 'Variant');
      setActiveLayerId(layerId);
      writeVariantPref(masterId, layerId);
      setDocState({
        key: `${masterId}::${layerId}::${nonce.current++}`,
        blocks: variantDocBlocks(result),
      });
    },
    [draft, masterId]
  );
  const switchRef = React.useRef(switchTo);
  switchRef.current = switchTo;

  const recompose = React.useCallback(() => {
    if (!activeRef.current) return;
    switchRef.current(activeRef.current);
  }, []);

  /* ─── First paint: the master's blocks, in the remembered variant ───
     Keyed on the IDENTITY of `initialBlocks`, which the draft hook changes
     only on a real reload (open / discard / revert) — never on a keystroke
     and never when the lazy checkout mints the draft row. Anything looser
     (a draft id, a length) resets the canvas mid-sentence. */
  const openedBlocks = React.useRef<ContentBlock[] | null>(null);
  const openedMaster = React.useRef('');
  React.useLayoutEffect(() => {
    if (draft.loadState !== 'ready') return;
    if (openedBlocks.current === draft.initialBlocks && openedMaster.current === masterId) return;
    openedBlocks.current = draft.initialBlocks;
    openedMaster.current = masterId;
    const remembered = readVariantPref(masterId);
    const exists = remembered && draft.currentLayers().some((l) => l.id === remembered);
    switchRef.current(exists ? remembered : null);
  }, [draft.loadState, draft.initialBlocks, masterId, draft]);

  /* ─── Cloud can win late: `Prefs.load` (app.tsx) is fire-and-forget, so
     the mount above may have already run against the localStorage seed
     before `Prefs.isReady()` flipped true. Re-apply the remembered variant
     exactly once, the first render after it becomes ready — same
     appliedRef + `Prefs.subscribe` shape as `usePref.ts`. Skipped when the
     cloud value matches what is already showing, so a session where the
     cloud was already ready at mount never re-triggers a canvas reset. */
  const cloudAppliedRef = React.useRef(false);
  const [, forceCloudTick] = React.useState(0);
  React.useEffect(() => Prefs.subscribe(() => forceCloudTick((t) => t + 1)), []);
  React.useEffect(() => {
    if (cloudAppliedRef.current || !Prefs.isReady() || draft.loadState !== 'ready') return;
    cloudAppliedRef.current = true;
    const remembered = readVariantPref(masterId);
    const exists = remembered && draft.currentLayers().some((l) => l.id === remembered);
    const resolved = exists ? remembered : null;
    if (resolved === activeRef.current) return;
    switchRef.current(resolved);
  });

  /* ─── The derive: document → layer ops ─── */
  const applyDoc = React.useCallback(
    (doc: JSONContent) => {
      const layerId = activeRef.current;
      const baseline = baselineRef.current;
      if (!layerId || !baseline) return;
      const current = draft.currentLayers();
      const previous = current.find((l) => l.id === layerId);
      if (!previous) return;
      const next = deriveLayer(doc, { baseline, previous });
      const view = viewRef.current;
      const overridden = new Set((next.overrides || []).map((o) => o.blockId));
      const stale = staleOverrideBlockIds(next, baseline);
      const changedPaint =
        !view ||
        view.overriddenIds.size !== overridden.size ||
        view.staleIds.size !== stale.size ||
        [...overridden].some((id) => !view.overriddenIds.has(id));
      if (view) {
        viewRef.current = { ...view, overriddenIds: overridden, staleIds: stale };
      }
      if (changedPaint) {
        // The decorations for THIS transaction were already built. Ask for one
        // more pass so a brand-new "Overridden" tag appears with the keystroke
        // that earned it, not with the next one.
        queueMicrotask(() => {
          const ed = getEditor();
          if (!ed || ed.isDestroyed) return;
          ed.view.dispatch(
            ed.state.tr.setMeta(VARIANT_REBUILD, true).setMeta('addToHistory', false)
          );
        });
      }
      if (sameLayer(previous, next)) return;
      draft.setLayers(current.map((l) => (l.id === layerId ? next : l)));
    },
    [draft, getEditor]
  );

  /* ─── Variant CRUD ─── */
  const createVariant = React.useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const layer = newClientLayer(undefined, trimmed, masterId);
      draft.setLayers([...draft.currentLayers(), layer]);
      // The layer is in the ref by now, so the switch composes against it.
      switchRef.current(layer.id);
      toastRef.current(`${trimmed} added — everything you type here lands on its own layer`);
    },
    [draft, masterId]
  );

  const duplicateVariant = React.useCallback(
    (layerId: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const source = draft.currentLayers().find((l) => l.id === layerId);
      if (!source) return;
      const layer = duplicateVariantLayer(source, trimmed, masterId);
      draft.setLayers([...draft.currentLayers(), layer]);
      switchRef.current(layer.id);
      toastRef.current(`${trimmed} created as a copy of ${source.name || 'this variant'}`);
    },
    [draft, masterId]
  );

  const deleteVariant = React.useCallback(
    (layerId: string) => {
      const next = draft.currentLayers().filter((l) => l.id !== layerId);
      draft.setLayers(next);
      writeVariantPref(masterId, null);
      switchRef.current(null);
      toastRef.current('Variant deleted — the master and every other variant are untouched');
    },
    [draft, masterId]
  );

  /* ─── Guard + tags ─── */
  const onGate = React.useCallback((reason: VariantGateReason) => {
    const now = Date.now();
    if (now - gateAt.current < 1500) return;
    gateAt.current = now;
    toastRef.current(
      reason === 'remove'
        ? "This edit isn't possible in a variant — hide the block instead (select its handle and press Delete, or right-click ▸ Hide in this variant)"
        : reason === 'group'
          ? 'This group comes from the master.'
          : "This edit isn't possible in a variant — move office-master blocks from the Office Master variant"
    );
  }, []);

  const setGhost = React.useCallback(
    (blockId: string, ghost: boolean) => {
      const ed = getEditor();
      if (!ed) return;
      setBlockGhost(ed.state, ed.view.dispatch.bind(ed.view), blockId, ghost);
      // These commands are almost always fired from a menu or a tag — the
      // click that triggered them left focus on that control, not the sheet.
      // The transaction above already mapped the selection; put the DOM focus
      // back where it belongs so the caret is visible again (§ caret cue).
      ed.commands.focus();
    },
    [getEditor]
  );

  const hideSelection = React.useCallback(() => {
    const ed = getEditor();
    const baseline = baselineRef.current;
    if (!ed || !baseline) return;
    const ok = hideSelectedBlocks(ed.state, ed.view.dispatch.bind(ed.view), (id) =>
      baseline.byId.has(id)
    );
    if (!ok)
      toastRef.current('Nothing to hide here — this block belongs to the variant’s own layer');
    ed.commands.focus();
  }, [getEditor]);

  const revert = React.useCallback(
    (blockId: string) => {
      const ed = getEditor();
      const base = baselineRef.current?.byId.get(blockId);
      if (!ed || !base) return;
      revertBlockToCore(ed.state, ed.view.dispatch.bind(ed.view), blockId, base.raw);
      setCompare(null);
      toastRef.current('Reverted to the office master text');
      ed.commands.focus();
    },
    [getEditor]
  );

  const openCompare = React.useCallback(
    (blockId: string, anchor: DOMRect) => {
      const base = baselineRef.current?.byId.get(blockId);
      const ed = getEditor();
      if (!base || !ed) return;
      let variantText = '';
      ed.state.doc.forEach((node) => {
        if ((node.attrs.blockId as string) === blockId) variantText = node.textContent;
      });
      setCompare({
        blockId,
        core: base.raw.text || '',
        variant: variantText,
        anchor: { left: anchor.left, bottom: anchor.bottom },
      });
    },
    [getEditor]
  );

  /** "Keep mine" — re-pin the override to the core as it stands now, so the
   *  indicator clears without touching the client's words. */
  const acknowledgeStale = React.useCallback(
    (blockId: string) => {
      const layerId = activeRef.current;
      const baseline = baselineRef.current;
      if (!layerId || !baseline) return;
      const base = baseline.byId.get(blockId);
      if (!base) return;
      const next = draft.currentLayers().map((l) =>
        l.id !== layerId
          ? l
          : {
              ...l,
              overrides: (l.overrides || []).map((o) =>
                o.blockId === blockId ? { ...o, base: makeOverrideBase(base.raw) } : o
              ),
            }
      );
      draft.setLayers(next);
      const layer = next.find((l) => l.id === layerId);
      if (layer && viewRef.current) {
        viewRef.current = { ...viewRef.current, staleIds: staleOverrideBlockIds(layer, baseline) };
      }
      setCompare(null);
      const ed = getEditor();
      if (ed && !ed.isDestroyed) {
        ed.view.dispatch(ed.state.tr.setMeta(VARIANT_REBUILD, true).setMeta('addToHistory', false));
        ed.commands.focus();
      }
      toastRef.current('Kept this variant’s wording');
    },
    [draft, getEditor]
  );

  const onTag = React.useCallback(
    (kind: 'override' | 'stale' | 'restore', blockId: string, anchor: DOMRect) => {
      if (kind === 'restore') {
        setGhost(blockId, false);
        return;
      }
      if (kind === 'stale') {
        openCompare(blockId, anchor);
        return;
      }
      revert(blockId);
    },
    [openCompare, revert, setGhost]
  );

  /* ─── Small readers the chrome and the context menu need ─── */
  const originOf = React.useCallback((blockId: string): 'core' | 'layer' => {
    const view = viewRef.current;
    if (!view) return 'core';
    return view.baselineIds.has(blockId) ? 'core' : 'layer';
  }, []);

  const isOverridden = React.useCallback(
    (blockId: string) => !!viewRef.current?.overriddenIds.has(blockId),
    []
  );

  const isGhost = React.useCallback(
    (blockId: string) => {
      const ed = getEditor();
      if (!ed) return false;
      let ghost = false;
      ed.state.doc.forEach((node) => {
        if ((node.attrs.blockId as string) !== blockId) return;
        ghost = (node.attrs.meta as Record<string, unknown> | undefined)?.__ghost === true;
      });
      return ghost;
    },
    [getEditor]
  );

  const activeLayer = layers.find((l) => l.id === activeLayerId) || null;

  return {
    layers,
    activeLayerId,
    activeLayer,
    docKey: docState.key,
    ready: !!docState.key,
    docBlocks: docState.blocks,
    getView: React.useCallback(() => viewRef.current, []),
    inVariant: !!activeLayerId,
    originOf,
    isOverridden,
    isGhost,
    switchTo,
    recompose,
    createVariant,
    duplicateVariant,
    deleteVariant,
    applyDoc,
    onGate,
    onTag,
    hideSelection,
    setGhost,
    revert,
    compare,
    closeCompare: React.useCallback(() => setCompare(null), []),
    acknowledgeStale,
  };
}
